import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { buildContactPath } from "@/lib/contactPath";

const SCROLL_THRESHOLD = 80;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const isHomepage = pathname === "/";
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!isHomepage) return;

    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHomepage]);

  const navLinks = [
    { name: "Services", href: "/services" },
    { name: "How It Works", href: "/how-it-works" },
    { name: "Pricing", href: "/pricing" },
    { name: "Managed Properties", href: "/usha-listings" },
    { name: "Blog", href: "/blog" },
    { name: "About", href: "/about" },
  ];

  const highlightedLink = { name: "Free Estimate", href: "/calculator" };

  // On homepage: invisible until scrolled, no pointer events while hidden
  // On other pages: standard slide-in from top on mount
  const headerMotion = isHomepage
    ? {
        initial: { opacity: 0 },
        animate: { opacity: scrolled ? 1 : 0 },
        transition: { duration: 0.5, ease: "easeOut" },
      }
    : {
        initial: { y: -80, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        transition: { duration: 0.5, ease: "easeOut" },
      };

  return (
    <motion.header
      {...headerMotion}
      className="fixed top-0 left-0 right-0 z-50"
      style={{ pointerEvents: isHomepage && !scrolled ? "none" : "auto" }}
    >
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 py-3 sm:py-4">
        <div className="glass-card rounded-2xl md:rounded-full px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <span className="font-serif text-lg sm:text-xl md:text-2xl text-foreground">
              Parvatii<span className="text-secondary">Stays</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to={highlightedLink.href}
              className="text-sm font-semibold text-foreground border border-border rounded-full px-4 py-1.5 hover:bg-foreground/5 transition-colors duration-200"
            >
              {highlightedLink.name}
            </Link>
            <Button asChild variant="default" className="btn-lift rounded-full px-6">
              <Link to={buildContactPath({ source: "navbar-desktop", intent: "get-started" })}>Get Started</Link>
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-1.5 text-foreground"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="md:hidden mt-3 glass-card rounded-xl p-4"
            >
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-lg font-medium text-foreground py-2"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link
                  to={highlightedLink.href}
                  className="text-lg font-semibold text-foreground py-2 border-b border-border"
                  onClick={() => setIsOpen(false)}
                >
                  Free Estimate ✦
                </Link>
                <Button asChild variant="default" className="btn-lift rounded-full mt-2">
                  <Link to={buildContactPath({ source: "navbar-mobile", intent: "get-started" })} onClick={() => setIsOpen(false)}>
                    Get Started
                  </Link>
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Navbar;
